import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Heading,
    Text,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon,
    VStack,
    Divider,
    Skeleton,
    SkeletonText,
    Alert,
    AlertIcon,
} from '@chakra-ui/react';
import axios from 'axios';

/**
 * In-account FAQ page (logged-in customer).
 *
 * Reads the tenant's OWN, admin-editable FAQs from the DB-backed public FAQ
 * endpoint (GET /api/faq/{laundryId}). This is the same content the tenant
 * manages from Admin → "FAQ Pages" (shop.tenant_faqs), so each tenant owns and
 * edits its own FAQ — nothing is hardcoded and no tenant affects another.
 *
 * The server resolves {{tokens}} (shop name, pricing, hours, etc.) before
 * returning, so answers arrive as plain resolved strings.
 *
 * Unlike the public FAQIndexPage this view has no SEO/marketing chrome
 * (no meta tags, tenant header, or "Schedule Pickup" CTA) — it's the account
 * help view, rendered as a simple accordion grouped by category.
 */
const AccountFAQPage = ({ laundryId }) => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!laundryId) {
            setLoading(false);
            return;
        }
        let alive = true;
        setLoading(true);
        setError(null);
        axios
            .get(`${process.env.REACT_APP_AWS_API_URL}/api/faq/${laundryId}`)
            .then((res) => {
                if (!alive) return;
                setCategories(res.data?.categories || []);
            })
            .catch((err) => {
                if (!alive) return;
                console.error('Error loading FAQs:', err);
                setError('Unable to load FAQs right now. Please try again later.');
            })
            .finally(() => {
                if (alive) setLoading(false);
            });
        return () => {
            alive = false;
        };
    }, [laundryId]);

    return (
        <Container maxW="container.lg" py={8}>
            <VStack spacing={8} align="stretch">
                <Box textAlign="center">
                    <Heading size="lg" color="blue.700" mb={2}>
                        Frequently Asked Questions
                    </Heading>
                    <Text color="gray.600">
                        Find answers to common questions about our service
                    </Text>
                </Box>

                <Divider />

                {loading && (
                    <VStack spacing={6} align="stretch">
                        {[1, 2, 3].map((i) => (
                            <Box key={i} bg="white" borderRadius="lg" p={6} boxShadow="sm">
                                <Skeleton height="24px" width="220px" mb={4} />
                                <SkeletonText noOfLines={4} spacing={3} />
                            </Box>
                        ))}
                    </VStack>
                )}

                {!loading && error && (
                    <Alert status="error" borderRadius="md">
                        <AlertIcon />
                        {error}
                    </Alert>
                )}

                {!loading && !error && categories.length === 0 && (
                    <Box bg="white" borderRadius="lg" p={6} boxShadow="sm" textAlign="center">
                        <Text color="gray.500">No FAQs are available yet.</Text>
                    </Box>
                )}

                {!loading && !error && categories.map((category) => (
                    <Box key={category.name} w="100%" bg="white" borderRadius="lg" p={6} boxShadow="sm">
                        <Heading size="md" mb={4} color="blue.600">
                            {category.name}
                        </Heading>
                        <Accordion allowMultiple>
                            {(category.faqs || []).map((faq) => (
                                <AccordionItem key={faq.slug} border="none" mb={2}>
                                    <AccordionButton
                                        bg="gray.50"
                                        borderRadius="md"
                                        _hover={{ bg: 'gray.100' }}
                                        _expanded={{ bg: 'blue.50', color: 'blue.700' }}
                                    >
                                        <Box flex="1" textAlign="left" fontWeight="medium">
                                            {faq.question}
                                        </Box>
                                        <AccordionIcon />
                                    </AccordionButton>
                                    <AccordionPanel
                                        pb={4}
                                        pt={3}
                                        px={4}
                                        fontSize="sm"
                                        color="gray.700"
                                        whiteSpace="pre-wrap"
                                    >
                                        {faq.answer}
                                    </AccordionPanel>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </Box>
                ))}
            </VStack>
        </Container>
    );
};

export default AccountFAQPage;
